import {
  FLOW_STEPS,
  type FlowStep,
  type FlowType,
  GUIDED_STEPS,
  type TypeaheadAction,
  type TypeaheadState,
} from "@/components/typeahead/types.ts";

/** Create a fresh initial state for the typeahead flow. */
export function createInitialState(): TypeaheadState {
  return {
    step: "idle",
    flowType: "meeting",
    triggerWord: null,
    context: null,
    venue: null,
    serviceProvider: null,
    partySize: null,
    contacts: [],
    timeDate: null,
    userConstraintRanges: null,
    skipCalendarConflicts: null,
    duration: null,
    location: null,
    title: null,
    connectorService: null,
    externalInputs: { whenProvided: false, durationProvided: false },
  };
}

/**
 * Whether the given step should be skipped for this flow type.
 *
 * | Step             | meeting | appointment | reservation | connector |
 * |------------------|---------|-------------|-------------|-----------|
 * | connectorService | skip    | skip        | skip        | show      |
 * | venue            | skip    | skip        | show        | skip      |
 * | serviceProvider  | skip    | show        | skip        | skip      |
 * | partySize        | skip    | skip        | show        | skip      |
 * | contacts         | show    | skip        | skip        | skip      |
 * | location         | show    | skip        | skip        | skip      |
 * | context/time/…   | show    | show        | show        | skip      |
 *
 * The connector flow is single-step: the context step is skipped too, because
 * its trigger ("Add") has no context suggestions and any other typed context
 * would silently flip the flow type and discard the service.
 */
export function shouldSkipStep(flowType: FlowType, step: FlowStep): boolean {
  // Only guided steps are ever skipped — structural steps ("sent",
  // "trigger_selected") must stay reachable or advanceStep can never finish.
  if (flowType === "connector") {
    return GUIDED_STEPS.includes(step) && step !== "connectorService";
  }
  switch (step) {
    case "connectorService":
      return true;
    case "venue":
      return flowType !== "reservation";
    case "serviceProvider":
      return flowType !== "appointment";
    case "partySize":
      return flowType !== "reservation";
    case "contacts":
      return flowType !== "meeting";
    case "location":
      return flowType === "appointment" || flowType === "reservation";
    default:
      return false;
  }
}

/**
 * Return the next guided-flow step after the given step.
 * Returns `null` if there is no next step (i.e. already at "sent").
 */
function nextStep(current: FlowStep): FlowStep | null {
  const idx = FLOW_STEPS.indexOf(current);
  if (idx === -1 || idx >= FLOW_STEPS.length - 1) return null;
  return FLOW_STEPS[idx + 1];
}

/**
 * Advance the state to the next guided-flow step, skipping steps
 * that are irrelevant for the current flow type OR already answered
 * out-of-band by an active omnibox enrichment (see {@link TypeaheadState.externalInputs}).
 */
function advanceStep(state: TypeaheadState): TypeaheadState {
  let step = state.step;
  while (true) {
    const next = nextStep(step);
    if (!next) return state;
    if (shouldSkipStep(state.flowType, next)) {
      step = next;
      continue;
    }
    // Skip steps whose answer has been supplied out-of-band by an active
    // enrichment. The mapping is intentionally narrow: the state machine
    // doesn't know which enrichment provided what, only that a field is
    // already answered.
    if (next === "time" && state.externalInputs.whenProvided) {
      step = next;
      continue;
    }
    if (next === "duration" && state.externalInputs.durationProvided) {
      step = next;
      continue;
    }
    return { ...state, step: next };
  }
}

/**
 * Reducer for the scheduling typeahead guided flow.
 *
 * Each action either transitions to a specific step, advances to the next
 * step, or resets the flow entirely.
 *
 * ```
 * idle → matching → trigger_selected → context → contacts → time → duration → location → title → sent
 * ```
 */
export function typeaheadReducer(state: TypeaheadState, action: TypeaheadAction): TypeaheadState {
  switch (action.type) {
    case "SET_STEP":
      return { ...state, step: action.step };

    case "SELECT_TRIGGER":
      return {
        ...state,
        triggerWord: action.triggerWord,
        externalInputs: action.externalInputs,
        step: "trigger_selected",
      };

    case "ADD_CONTACT":
      return {
        ...state,
        contacts: [...state.contacts, action.contact],
      };

    case "REMOVE_CONTACT":
      return {
        ...state,
        contacts: state.contacts.filter((_, i) => i !== action.contactIndex),
      };

    case "SET_CONTEXT":
      return advanceStep({
        ...state,
        context: action.context,
        flowType: action.flowType,
        // Clear flow-type-specific state when flow type changes
        venue: action.flowType === state.flowType ? state.venue : null,
        serviceProvider: action.flowType === state.flowType ? state.serviceProvider : null,
        partySize: action.flowType === state.flowType ? state.partySize : null,
        contacts: action.flowType === state.flowType ? state.contacts : [],
        connectorService: action.flowType === state.flowType ? state.connectorService : null,
      });

    case "SET_CONNECTOR_SERVICE":
      return advanceStep({ ...state, connectorService: action.connectorService });

    case "SET_PARTY_SIZE":
      return advanceStep({ ...state, partySize: action.partySize });

    case "SET_VENUE":
      return advanceStep({ ...state, venue: action.venue });

    case "SET_SERVICE_PROVIDER":
      return advanceStep({ ...state, serviceProvider: action.serviceProvider });

    case "SET_TIME":
      return advanceStep({
        ...state,
        timeDate: action.timeDate,
        userConstraintRanges: action.userConstraintRanges ?? null,
        skipCalendarConflicts: action.skipCalendarConflicts ?? null,
      });

    case "SET_DURATION":
      return advanceStep({ ...state, duration: action.duration });

    case "SET_LOCATION": {
      const newState = { ...state, location: action.location };
      // In-person without an address is an intermediate state (address input still open)
      if (action.location.type === "in-person" && !action.location.details) {
        return newState;
      }
      return advanceStep(newState);
    }

    case "SET_TITLE":
      return advanceStep({ ...state, title: action.title });

    case "SKIP_STEP":
      // Default to "30 min" when skipping past the duration step without an explicit selection.
      if (state.step === "duration" && state.duration === null) {
        return advanceStep({ ...state, duration: "30 min" });
      }
      return advanceStep(state);

    case "RESET":
      return createInitialState();

    default:
      return state;
  }
}
